import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { t } from "../../lib/i18n";
import { toast } from "sonner";

export default function ApprovalAdmin({ players, refresh }) {
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/tournaments/pending");
      setPending(r.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const playerByPid = (pid) => players.find((p) => p.id === pid);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/tournaments/${id}/approve`);
      toast.success(t.approvedToast);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(t.failed);
    } finally { setBusyId(null); }
  };
  const reject = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/tournaments/${id}/reject`);
      toast.success(t.rejectedToast);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(t.failed);
    } finally { setBusyId(null); }
  };

  return (
    <div className="mt-8" data-testid="approval-admin">
      <div className="flex items-center gap-3 mb-5">
        <h2 className="font-display uppercase italic text-2xl">{t.pendingTournaments}</h2>
        <span className="text-[10px] font-mono px-2 py-1 border border-[#E2FF00]/40 text-[#E2FF00] uppercase tracking-[0.25em] font-bold">
          {pending.length}
        </span>
      </div>
      {pending.length === 0 ? (
        <p className="text-white/40 font-mono text-sm" data-testid="approval-empty">
          {t.noPending}
        </p>
      ) : (
        <div className="space-y-3">
          {pending.map((tt) => {
            const mod = tt.moderator_player_ids && tt.moderator_player_ids.length
              ? tt.moderator_player_ids.map((id) => playerByPid(id)).filter(Boolean)
              : (tt.moderator_player_id ? [playerByPid(tt.moderator_player_id)].filter(Boolean) : []);
            return (
              <div
                key={tt.id}
                data-testid={`pending-${tt.id}`}
                className="border border-white/10 bg-[#0C0C14] p-5 flex flex-col md:flex-row gap-4 md:items-center"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#E2FF00]/15 text-[#E2FF00] border border-[#E2FF00]/40">
                      {t.community}
                    </span>
                    <span className="text-[10px] font-mono text-white/40 uppercase tracking-[0.2em]">
                      {tt.format}
                    </span>
                    <span className="text-[10px] font-mono text-white/40">·</span>
                    <span className="text-[10px] font-mono text-white/40">
                      {(tt.player_ids || []).length}/{tt.max_players}
                    </span>
                  </div>
                  <div className="font-display uppercase italic text-xl mt-1 truncate">{tt.name}</div>
                  <div className="text-xs text-white/50 mt-1 font-mono">
                    {t.hostHandle}: <span className="text-white/80">{tt.host_handle}</span>
                    {mod.length > 0 && (
                      <>
                        {" · "}
                        {t.moderator}: <span className="text-[#00E5FF]">{mod.map((m) => m.handle).join(", ")}</span>
                      </>
                    )}
                  </div>
                  {tt.description && (
                    <p className="text-xs text-white/50 mt-2 line-clamp-2">{tt.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <a
                    href={`/tournaments/${tt.id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`pending-preview-${tt.id}`}
                    className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white/70 hover:text-white"
                  >
                    {t.bracket}
                  </a>
                  <button
                    onClick={() => approve(tt.id)}
                    disabled={busyId === tt.id}
                    data-testid={`approve-${tt.id}`}
                    className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/25 disabled:opacity-40"
                  >
                    {t.approve}
                  </button>
                  <button
                    onClick={() => reject(tt.id)}
                    disabled={busyId === tt.id}
                    data-testid={`reject-${tt.id}`}
                    className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-[#FF0055]/10 border border-[#FF0055]/40 text-[#FF0055] hover:bg-[#FF0055]/20 disabled:opacity-40"
                  >
                    {t.reject}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
